import React, { useEffect } from 'react'
import styled from 'styled-components'

import breakpoints from '../../constants/breakpoints'
import { ModalConsumer } from '../../context/ModalContext'
import i18n from '../../i18n'
import { ImageType, Info, Slide, VideoType, Skipsteps, ModalLink } from '../../types'
import { getNextSkipRoute } from '../../utils'
import SlideContainer from '../Layout/SlideContainer'
import LinkButton from '../LinkButton'
import Video from '../Video'
import Readspeaker from '../Readspeaker'

interface ExplanationProps {
  align?: string
  titleAlign?: string
  title: string
  description: string
  videos?: VideoType[]
  image?: ImageType
  info?: Info[]
  button: string
  nextLink: string
  routeIndex?: string
  routes: Slide[]
  modalLink: string
  modalHeader: string
  modalText: string
  modalLinks?: ModalLink[]
  skipSteps?: Skipsteps
  onSkipRoute: (path: string) => void
}

const Explanation: React.FC<ExplanationProps> = ({
  align,
  titleAlign,
  title,
  description,
  info,
  image,
  videos,
  button,
  nextLink,
  routeIndex,
  routes,
  modalLink,
  modalHeader,
  modalText,
  modalLinks,
  skipSteps,
  onSkipRoute,
}) => {
  const hasTable = description.includes('<table')

  useEffect(() => {
    const nextRoute = routeIndex && getNextSkipRoute(routeIndex, routes)
    nextRoute && onSkipRoute(nextRoute.path)
  }, [])

  return (
    <SlideContainer>
      <Content className="text-formatted" $align={align} $hasTable={hasTable}>
        <H1 $align={titleAlign}>{title}</H1>
        <Readspeaker align={titleAlign || align} />
        <Description>
          <ModalConsumer>
            {({ openModal }) => (
              <ContentContainer
                openModal={openModal}
                description={description}
                modalLink={modalLink}
                modalHeader={modalHeader}
                modalText={modalText}
                modalLinks={modalLinks}
              />
            )}
          </ModalConsumer>
        </Description>

        {info &&
          info.map((item, index) => (
            <ModalConsumer key={index}>
              {({ openModal }) => (
                <StyledLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    openModal(item.title, item.content)
                  }}
                >
                  {item.urlText ? item.urlText : i18n.t('explanation.moreInfo')}
                </StyledLink>
              )}
            </ModalConsumer>
          ))}

        {image && (
          <MediaWrapper>
            <Image src={image.src} alt={image.alt} />
          </MediaWrapper>
        )}

        {videos &&
          videos.map((video, index) => (
            <MediaWrapper key={index}>{<Video {...video} />}</MediaWrapper>
          ))}

        {nextLink && (
          <Next>
            <LinkButton to={nextLink}>{button}</LinkButton>
          </Next>
        )}

        {skipSteps && (
          <Skip>
            <LinkButton border={true} to={skipSteps.url}>
              {skipSteps.text ? skipSteps.text : i18n.t('skipSteps.text')}
            </LinkButton>
          </Skip>
        )}
      </Content>
    </SlideContainer>
  )
}

interface ContentContainerProps {
  openModal: (title: string, content: string) => void
  description: string
  modalLink: string
  modalHeader: string
  modalText: string
  modalLinks?: ModalLink[]
}

const convertKeyToId = (key: string) => (key === 'MODALLINK' ? 'modallink' : `modallink-${key}`) // backwards compatibility

const ContentContainer: React.FC<ContentContainerProps> = ({
  openModal,
  description,
  modalLink,
  modalHeader,
  modalText,
  modalLinks,
}) => {
  // Add modalLink into modalLinks for backwards compatibility
  const modalLinksToUse: ModalLink[] = [
    ...(modalLinks ? modalLinks : []),
    ...(modalLink
      ? [
          {
            key: 'MODALLINK',
            title: modalHeader,
            link: modalLink,
            content: modalText,
          },
        ]
      : []),
  ]
  // Update description with modal links
  const elements = modalLinksToUse.reduce((result, ml) => {
    const keyInDescription = `%${ml.key}%`
    const actualLink = `<a href="#" id="${convertKeyToId(ml.key)}">${ml.link}</a>`

    return result.replace(keyInDescription, actualLink)
  }, description)

  useEffect(() => {
    // Add click listeners
    for (const ml of modalLinksToUse) {
      const link = document.querySelector(`#${convertKeyToId(ml.key)}`)
      if (link) {
        link.addEventListener('click', (e) => {
          e.preventDefault()
          openModal(ml.title, ml.content)
        })
      }
    }

    return () => {
      // Remove click listeners
      for (const ml of modalLinksToUse) {
        const link = document.querySelector(`#${convertKeyToId(ml.key)}`)
        if (link) {
          link.removeEventListener('click', (e) => {
            e.preventDefault()
            openModal(ml.title, ml.content)
          })
        }
      }
    }
  }, [])

  return <div dangerouslySetInnerHTML={{ __html: elements }}></div>
}

const Content = styled.div<{ $align: string | undefined; $hasTable: boolean }>`
  text-align: ${({ $align }) => $align || 'center'};
  max-width: 100%;
  width: 100%;

  @media (min-width: ${breakpoints.sm}) {
    max-width: ${({ $hasTable }) => ($hasTable ? '100%' : '700px')};
  }

  ul {
    margin-top: 0;
  }
`

const H1 = styled.h1<{ $align: string | undefined }>`
  text-align: ${({ $align }) => $align && $align};
`

const Description = styled.div`
  margin: 0 0 10px;
  font-size: 1.125rem;

  & .img-wrapper {
    margin: 40px 0;

    & img {
      max-width: 100%;
      margin: 0 20px 20px 0;
    }

    @media (min-width: ${breakpoints.md}) {
      display: flex;
      flex-direction: row;

      img {
        max-width: 570px;
      }
    }
  }

  & .img-wrapper-lg {
    margin: 40px 0;

    & img {
      max-width: 100%;
    }
  }
`

const StyledLink = styled.a`
  color: ${({ theme }) => theme.blue};
  text-decoration: underline;
  display: block;
  width: fit-content;
`

const MediaWrapper = styled.div`
  max-width: 700px;
  margin: 0 auto;
`

const Image = styled.img`
  margin-top: 35px;
  display: block;
  width: 100%;
  height: auto;
`

const Next = styled.div`
  margin-top: 35px;
  width: 100%;

  @media (min-width: ${breakpoints.md}) {
    margin: 35px auto;
    max-width: 700px;
  }
`

const Skip = styled.div`
  margin-top: 35px;
  width: 100%;

  @media (min-width: ${breakpoints.md}) {
    margin: 35px auto;
    max-width: 700px;
  }
`

export default Explanation
